import React, { ButtonHTMLAttributes, ReactNode } from 'react';

import { classNames } from '@repo/ui/src/utils/classNames';
import SkewerIcon from '@repo/ui/src/components/Icons/SkewerIcon';
import KnifeIcon from '@repo/ui/src/components/Icons/KnifeIcon';

interface Base {
  children: ReactNode;
}

export enum ButtonVariant {
  primary = 'primary',
  secondary = 'secondary',
  tertiary = 'tertiary',
  knife = 'knife',
  skewer = 'skewer',
  bare = 'bare',
}
export interface BaseButtonProps extends Base {
  className?: string;
  variant?: ButtonVariant;
  active?: boolean;
  type?: 'submit' | 'reset' | 'button';
  size?: 'large' | 'medium' | 'small' | 'none';
  full?: boolean;
  as?: React.ComponentType<any>;
  dark?: boolean;
  negative?: boolean;
}
export type ButtonProps<ComponentType = 'button'> = (ComponentType extends React.ComponentType<
  infer PropsType
>
  ? PropsType
  : ButtonHTMLAttributes<HTMLButtonElement>) &
  BaseButtonProps & { as?: ComponentType };
type ButtonType = <T>(props: ButtonProps<T>) => JSX.Element;

const Button: ButtonType = ({
  className,
  variant = ButtonVariant.primary,
  size = 'medium',
  children,
  active,
  as: Component = 'button',
  full,
  dark = false,
  negative = false,
  ...rest
}) => {
  const special = variant === 'knife' || variant === 'skewer';
  return (
    <Component
      aria-pressed={active}
      data-variant={variant}
      className={classNames(
        'tra-text-web-utl-button tw-rounded tw-group tw-inline-block tw-cursor-pointer',
        variant === 'primary' && 'tra-btn-primary',
        variant === 'secondary' && 'tra-btn-secondary',
        variant === 'tertiary' && 'tra-btn-tertiary',
        variant === 'bare' && 'tra-btn-bare',
        (variant === 'skewer' || variant === 'knife') && 'tw-grid tw-grid-flow-col tw-items-center',
        special && 'tra-btn-special !tw-px-0',
        size === 'large' && 'tw-py-4 tw-px-12 tra-text-web-utl-button-l',
        size === 'medium' && 'tw-py-3 tw-px-10 tra-text-web-utl-button-m',
        size === 'small' && 'tw-py-1 tw-px-6 tra-text-web-utl-button-s',
        (size === 'small' && variant === 'primary') && 'tw-font-bold',
        special && dark && 'tw-text-white',
        full ? 'tw-w-full' : 'tw-w-fit',
        negative && 'negative',
        // @ts-expect-error disabled might not exist
        rest.disabled && Component !== 'button' && 'tra-disabled',
        className,
      )}
      {...rest}
    >
      {children}
      {variant === 'skewer' && <SkewerIcon className="tra-btn-icon-special tw-text-brand-300" />}
      {variant === 'knife' && <KnifeIcon className="tra-btn-icon-special tw-text-brand-300" />}
    </Component>
  );
};

export default Button;
